// The Songstats diagnostic read + the two diagnostic actions behind `/debug`.
//
// These go through a RAW fetch rather than the generated client on purpose: the backend controller is
// `[ApiExplorerSettings(IgnoreApi)]`, so its routes are deliberately absent from `openapi.json` and therefore
// from the generated schema. Keeping them out of the contract is what stops a temporary debug surface from
// entering the committed snapshot and tripping CI's drift gates (no re-export, no codegen) — the cost is that
// the response types are declared by hand here. They mirror `SongstatsDiagnosticsModels.cs`.
//
// The routes only exist in non-Production hosts (`[InternalController]`), so this page 404s in PROD by design.
import { useQueryClient } from "@tanstack/react-query";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";
import { API_URL } from "../../config/env.js";

// ---- the wire shapes (hand-mirrored from the backend records) ------------------------------------------

export type VerdictStatus = "ok" | "warn" | "fail";

export interface ChainVerdict {
  step: number;
  name: string;
  status: VerdictStatus;
  detail: string;
}

export interface VendorKeyState {
  keyConfigured: boolean;
  keyFingerprint: string | null;
  keyLength: number;
  baseUrl: string;
  providerPaused: boolean;
}

export interface SpotifyClaimState {
  connectionPresent: boolean;
  status: string | null;
  externalAccountId: string | null;
  displayHandle: string | null;
  connectedOn: string | null;
  lastSyncOn: string | null;
  lastError: string | null;
}

export interface ScheduleState {
  provider: string;
  kind: string;
  nextRunOn: string | null;
  lastRunOn: string | null;
  lastStatus: string | null;
  lastError: string | null;
  paused: boolean;
  contentHashPrefix: string | null;
  due: boolean;
}

export interface CadenceState {
  dailyCap: number;
  minIntervalMinutes: number;
  windowOpenHourUtc: number;
  spentToday: number;
  currentHourUtc: number;
  windowOpen: boolean;
  preFetchVerdict: string;
  wouldRunAt: string | null;
}

export interface PlatformDocState {
  platform: string;
  asOf: string;
  topLevelKeys: string[];
  payload: string;
}

export interface RadioDocState {
  asOf: string | null;
  topLevelKeys: string[];
  payload: string;
}

export interface AudienceCitySample {
  city: string;
  region: string;
  country: string;
  audience: number;
  asOf: string | null;
}

export interface SnapshotSample {
  platform: string | null;
  metric: string;
  value: number;
  asOf: string | null;
}

export interface TrackStatSample {
  trackKey: string;
  title: string | null;
  spotifyTrackId: string | null;
  popularity: number | null;
  streams28d: number | null;
  streamsLifetime: number | null;
  asOf: string | null;
}

export interface LandingState {
  platformDocs: PlatformDocState[];
  radio: RadioDocState | null;
  audienceCityCount: number;
  audienceCitiesAsOf: string | null;
  audienceCitiesSample: AudienceCitySample[];
  snapshotCount: number;
  snapshotsAsOf: string | null;
  snapshotsLatest: SnapshotSample[];
  trackStatCount: number;
  trackStatsSample: TrackStatSample[];
}

export interface RawPayloadState {
  endpoint: string;
  fetchedOn: string;
  bytes: number;
  contentHashPrefix: string | null;
  payload: string;
}

export interface ComposedReadState {
  dashboard: unknown;
  music: unknown;
}

export interface SongstatsDiagnosticsDoc {
  bandId: string;
  generatedOnUtc: string;
  verdicts: ChainVerdict[];
  config: VendorKeyState;
  claim: SpotifyClaimState;
  schedules: ScheduleState[];
  cadence: CadenceState;
  landings: LandingState;
  rawPayloads: RawPayloadState[];
  reads: ComposedReadState;
}

export interface ProbeCall {
  key: string;
  url: string;
  primary: boolean;
  outcome: string;
  httpStatus: number | null;
  elapsedMs: number;
  bytes: number;
  error: string | null;
  topLevelKeys: string[];
  body: string | null;
  /** The refuser's requested wait, when it sent one. */
  retryAfter: string | null;
  /** Who answered. `cloudflare` here on a 4xx/5xx means an edge did, not the vendor. */
  server: string | null;
  /** Cloudflare's request id. Present on a refusal ⇒ the call may never have reached the vendor. */
  cfRay: string | null;
}

export interface ProbeShaping {
  combinedPayload: string;
  platformDoc: string;
  platformDocKeys: string[];
  snapshots: SnapshotSample[];
  cityCount: number;
  citiesSample: AudienceCitySample[];
  radioDoc: string | null;
  radioDocKeys: string[];
  verdicts: ChainVerdict[];
}

export interface SongstatsProbeDoc {
  bandId: string;
  generatedOnUtc: string;
  baseUrl: string;
  artistId: string | null;
  keyConfigured: boolean;
  /** Why the probe did NOT complete. Null when it ran end to end. */
  skipped: string | null;
  calls: ProbeCall[];
  shaped: ProbeShaping | null;
  /** A caveat about a probe that DID run — e.g. the dev artist pin. Not a failure. */
  notice: string | null;
}

export interface SongstatsRefreshResult {
  queued: number;
  contentHashCleared: boolean;
  detail: string;
}

// ---- the transport ---------------------------------------------------------------------------------------

const DEBUG_KEY = ["songstats-debug"] as const;

/** The band-direct debug base. Deliberately NOT org-lens-rewritten: this is a diagnostic on one band. */
const debugUrl = (bandId: string, suffix = "") =>
  `${API_URL}/api/v1/bands/${bandId}/debug/songstats${suffix}`;

/**
 * A raw authenticated call to a route the generated client does not know about. It reuses the app's
 * TokenProvider bearer (never `localStorage` — the lint-enforced rule) and `credentials: "include"` so it
 * behaves exactly like a generated-client call, minus the types.
 */
async function debugFetch<T>(bearer: string | null, url: string, method: "GET" | "POST"): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
  });

  if (!response.ok) {
    // A 404 here is the expected shape in Production, where [InternalController] removes the route.
    let problem: unknown;
    try {
      problem = await response.json();
    } catch {
      problem = undefined;
    }
    throw new ApiError(response.status, problem);
  }

  const body = (await response.json()) as { data?: T };
  if (!body?.data) throw new ApiError(response.status, undefined);
  return body.data;
}

/** GET the whole chain. No vendor call, no writes — safe to poll while iterating. */
export function useSongstatsDiagnostics() {
  const { tokenProvider } = useApp();
  return useBandQuery<SongstatsDiagnosticsDoc>(
    DEBUG_KEY,
    (bandId) => debugFetch<SongstatsDiagnosticsDoc>(tokenProvider.getBearer(), debugUrl(bandId), "GET"),
    // No staleTime: the whole point is to see the CURRENT state after an action.
    { staleTime: 0, retry: false },
  );
}

/** POST the live probe — calls Songstats now and shapes the answer, landing nothing. */
export function useSongstatsProbe() {
  const { tokenProvider } = useApp();
  return useBandMutation<SongstatsProbeDoc, void>((bandId) =>
    debugFetch<SongstatsProbeDoc>(tokenProvider.getBearer(), debugUrl(bandId, "/probe"), "POST"),
  );
}

/** POST a forced refresh — makes the schedules due; optionally clears the change-detection baseline. */
export function useSongstatsRefresh() {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<SongstatsRefreshResult, { clearContentHash: boolean }>(
    (band, { clearContentHash }) =>
      debugFetch<SongstatsRefreshResult>(
        tokenProvider.getBearer(),
        debugUrl(band, `/refresh?clearContentHash=${clearContentHash}`),
        "POST",
      ),
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, ...DEBUG_KEY] }) },
  );
}

// ---- the DEV-ONLY fixture connect (Settings › Connections) ------------------------------------------------

export interface DevArtistConnectResult {
  connected: boolean;
  detail: string;
}

/**
 * Connect this band to the fixture artist. DEV ONLY — the API refuses outside a recognised non-production
 * lane, and the button that calls it is not rendered outside one either.
 *
 * It exists because the REAL path cannot work here: Spotify's OAuth claim answers 409 `spotify-not-connected`
 * for arbitrary bands under the Feb 2026 development-mode allowlist, so without this there is no way to attach
 * a dev band to the analytics spine through the product at all.
 *
 * Invalidates the connections read so the card repaints, and the debug chain so `/debug` agrees.
 */
export function useConnectDevArtist() {
  const { tokenProvider } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<DevArtistConnectResult, void>(
    (band) =>
      debugFetch<DevArtistConnectResult>(
        tokenProvider.getBearer(),
        `${API_URL}/api/v1/bands/${band}/connections/spotify/dev-artist`,
        "POST",
      ),
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "connections"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, ...DEBUG_KEY] });
      },
    },
  );
}
